import { gql, type ApolloCache, type FieldPolicy } from "@apollo/client";
import type { MockedResponse } from "@apollo/client/testing";

interface GenerateUserMockOptions {
  variables?: Record<string, unknown> | null;
  result?: Record<string, unknown> | null;
  error?: Error | null;
}

const userNodeFields = (profileNodeFields: string) => `
  id
  first_name
  last_name
  email
  last_login
  saml_group_profiles {
    saml_group
    profile {
      id
      name
    }
  }
  identity_provider_source
  profiles {
    edges {
      node {
        ${profileNodeFields}
      }
    }
  }
  role {
    id
    name
  }
`;

const USER_NODE_FIELDS = userNodeFields(`
  id
  name
  scopes {
    edges {
      node {
        id
        name
      }
    }
  }
  scope_groups {
    edges {
      node {
        id
        name
      }
    }
  }
`);

// Paged list omits per-profile scopes: the page derives hasScopes from GET_PROFILES.
const USER_PAGE_NODE_FIELDS = userNodeFields(`
  id
  name
`);

export const GET_USERS = gql`
  query getUsers($filters: UserFilter) {
    users(filters: $filters) {
      edges {
        node {
          ${USER_NODE_FIELDS}
        }
      }
    }
  }
`;

export const GET_USERS_PAGE = gql`
  query getUsersPage(
    $first: Int
    $after: String
    $sort: [UserSortEnum]
    $filters: UserFilter
  ) {
    users(first: $first, after: $after, sort: $sort, filters: $filters) {
      total_count
      edges {
        node {
          ${USER_PAGE_NODE_FIELDS}
        }
      }
    }
  }
`;

// Unnormalized connection: `false` replaces it wholesale (no "cache data may be lost"
// warning) and keeps total_count, which an edges-only merge would drop.
// Keyed by args by default, so the full list, the self lookup and each page are separate entries.
export const usersCacheFieldPolicy = {
  merge: false,
} as const satisfies FieldPolicy;

// Drops every cached users list (full list and pages) so cache-first readers refetch.
export const evictUsersCache = (cache: ApolloCache<unknown>) => {
  cache.evict({ fieldName: "users" });
  cache.gc();
};

export const generateUsersPageData = ({
  variables = null,
  result = null,
  error = null,
}: GenerateUserMockOptions): MockedResponse => {
  const usersPageMock: MockedResponse = {
    request: {
      query: GET_USERS_PAGE,
      variables: variables ?? undefined,
    },
  };

  if (error) {
    return { ...usersPageMock, error };
  }

  if (result) {
    return { ...usersPageMock, result: { data: result } };
  }

  return { ...usersPageMock, result: {} };
};

export const INVITE_USER = gql`
  mutation inviteUser($send_email: Boolean, $user: UserInput!) {
    invite_user(send_email: $send_email, user: $user) {
      ok
      msg
      new_user {
        id
        email
        last_name
        first_name
        last_login
      }
    }
  }
`;

export const generateAddUser = ({
  variables = null,
  result = null,
  error = null,
}: GenerateUserMockOptions): MockedResponse => {
  let addUserMock: MockedResponse = {
    request: {
      query: INVITE_USER,
      // cast preserves original runtime behavior (variables may be null) without widening to any
      variables: variables as Record<string, unknown> | undefined,
    },
  };

  if (error) {
    return {
      ...addUserMock,
      error: error,
    };
  }

  if (result) {
    return {
      ...addUserMock,
      result: { data: result },
    };
  }

  return {
    ...addUserMock,
    result: {},
  };
};

export const REINVITE_USER = gql`
  mutation reinviteUser($user_id: String!) {
    mgnt_reset_user_password(user_id: $user_id) {
      ok
      msg
    }
  }
`;

export const DELETE_USER = gql`
  mutation deleteUser($user_id: String!) {
    delete_user(user_id: $user_id) {
      ok
      msg
    }
  }
`;

export const EDIT_USER = gql`
  mutation editUser(
    $user_id: String!
    $first_name: String!
    $last_name: String!
    $role_id: String
    $profile_ids: [String]
  ) {
    change_user_fields(
      user_id: $user_id
      first_name: $first_name
      last_name: $last_name
      role_id: $role_id
      profile_ids: $profile_ids
    ) {
      ok
      msg
    }
  }
`;

export const generateEditUser = ({
  variables = null,
  result = null,
  error = null,
}: GenerateUserMockOptions): MockedResponse => {
  let editUserMock: MockedResponse = {
    request: {
      query: INVITE_USER,
      // cast preserves original runtime behavior (variables may be null) without widening to any
      variables: variables as Record<string, unknown> | undefined,
    },
  };

  if (error) {
    return {
      ...editUserMock,
      error: error,
    };
  }

  if (result) {
    return {
      ...editUserMock,
      result: { data: result },
    };
  }

  return {
    ...editUserMock,
    result: {},
  };
};
